import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import MaterialTable from 'material-table';
import {
    AppBar, Button, createMuiTheme, Toolbar, Typography, CssBaseline, CardContent, Backdrop, CircularProgress, Grid, Fab
} from '@material-ui/core';
import { ThemeProvider } from '@material-ui/styles';
import { makeStyles } from '@material-ui/core/styles';
import 'date-fns';
import { GlobalTheme } from '../../layouts/theme';
import { Theme } from '../contexts/themeContext';
import { Context } from '../contexts/siteContext';
import Card from '../card/card.js';
import CardBody from '../card/cardBody.js';
import SmallMenu from '../header/smallMenu';
import StatusIcon from '../common/statusIcon';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Fade from '@material-ui/core/Fade';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import ContentDialog from '../common/contentDialog.js';
import EscalationList from './escalationList.js';
import { HttpService } from '../../apiAuthorization/httpService';

const useStyles = makeStyles(theme => ({
    root: {
        '& > *': {
            margin: '-10px 0px 10px 0px',
            width: '100%'
        }
    },
    button: {
        width: '15%',
        float: 'right'
    },
    error: {
        fontSize: '12px',
        fontWeight: "bold",
        color: 'red',
        marginTop: '-5px'
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff'
    },
    dashboardTitle: {
        flex: 1,
    },
    title: {
        flex: 1,
        color: '#000'
    },
    banner: {
        backgroundColor: 'yellow',
        fontSize: 'medium',
        textAlign: 'center',
        padding: '5px',
        color: 'red',
        cursor: 'pointer'
    },
    card: {
        marginTop: '0px',
        marginBottom: '0px',
        backgroundColor: '#fff',
    }
}));

const MenuPopupState = props => {
    
    const [anchorEl, setAnchorEl] = useState(null);
    const [options, setOptions] = useState([]);

    const handleClick = event => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (selected) => {
        setAnchorEl(null);
        if (selected === -1) return;
        selected = 'Available' === selected ? null : selected;

        let beforePreDeterminedDuty = !props.data.PreDeterminedDuty ? "Available": props.data.PreDeterminedDuty
        let currentPreDeterminedDuty = !selected ? "Available" : selected;
        props.onMoveItem(props.data.Id, selected, currentPreDeterminedDuty, beforePreDeterminedDuty);
    };

    const getOptions =() =>{
        let arr = [];
        if (props.status === "Available") {
            arr = [
                'Training', 'Department meeting', 'Straighten and organize supply rooms', 'Stocking carts', 'Deliver specimens to lab or via tube-system',
                'Comfort Rounds in emergency room lobbies, waiting areas, etc.', 'Round on POA staffed rooms to ensure they have what they need',
                'Assist with holding patients during nursing procedures, i.e. PIV insertion, suctioning etc.', 'Assist in Patient Transport',
                'Other duties as assigned',
                'Assist with patient lift/ambulation',
                'Retrieve equipment',
                'Deliver equipment'
            ];
        } else if (props.status === "Other") {
            arr = ['Available'];
        }
        setOptions(arr);
    }

    const open = Boolean(anchorEl);

    useEffect(() => {
        getOptions();
        // eslint-disable-next-line 
    }, []);

    return (
        <div>
            <Fab size="small" color="primary" onClick={handleClick}>
                <SwapHorizIcon />
            </Fab>
            
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => { handleClose(-1)}}
                TransitionComponent={Fade}
            >
                {
                    options.map((item,index) => {
                        return <MenuItem key={`${index} + '-' + ${index}`} onClick={() => { handleClose(item)}}>{item}</MenuItem>
                   })
                }
            </Menu>
        </div>
    );
    
}

export default function ObservationDashboard(props) {
    const classes = useStyles();
    const { defaultSite } = useContext(Context);
    const { theme } = useContext(Theme);
    const currentTheme = theme ? createMuiTheme(GlobalTheme[theme]) : createMuiTheme(GlobalTheme[localStorage.getItem("theme")]);
    const [backDrop, setBackDrop] = useState(false);
    const [isShowEscalations, setIsShowEscalations] = useState(false);
    const tableStyleBase = { boxShadow: 'none' };
    const posGroupId = defaultSite.IntegratedSystem.POSGroupId;

    const rowOrangeStyle = (rowData, rowIndex) => {
        return {
            backgroundColor: rowData.IsDelayed? 'orange' : currentTheme.alternateGridRow
        };
    };

    const [availableData, setAvailableData] = useState({
        columns: [
            { title: 'Attendant', field: 'Attendant' },
            { title: 'Time', field: 'DurationSeconds', defaultSort: 'desc', render: rowData => rowData.DurationTime},
        ],
        data: []
    });

    const [onBreakData, setOnBreakData] = useState({
        columns: [
            { title: 'Attendant', field: 'Attendant' },
            { title: 'Time', field: 'DurationSeconds', defaultSort: 'desc', render: rowData => rowData.DurationTime },
            { title: 'Break Type', field: 'BreakType' },
        ],
        data: []
    });

    const [assignData, setAssignData] = useState({
        columns: [
            { title: 'Attendant', field: 'Attendant'},
            { title: 'Time', field: 'DurationSeconds', defaultSort: 'desc', render: rowData => rowData.DurationTime },
            { title: 'Location', field: 'LocationPath', },
            { title: 'Patient Name', field: 'PatientName' },
            { title: (posGroupId === 1) ? 'Risk' : 'Observation Reasons', field: (posGroupId === 1) ? 'RiskName' : 'ObservationReasons', render: rowData => rowData.ISHigh ? <label style={{ color: 'red' }}>{(posGroupId === 1) ? rowData.RiskName : rowData.ObservationReasons}</label> : (posGroupId === 1) ? rowData.RiskName : rowData.ObservationReasons }
        ],
        data: []
    });
    const [activeData, setActiveData] = useState({
        columns: [
            { title: 'Attendant', field: 'Attendant' },
            { title: 'Time', field: 'DurationSeconds', defaultSort: 'desc', render: rowData => rowData.DurationTime},
            { title: 'Location', field: 'LocationPath', },
            { title: 'Patient Name', field: 'PatientName' },
            { title: (posGroupId === 1) ? 'Risk' : 'Observation Reasons', field: (posGroupId === 1) ? 'RiskName' : 'ObservationReasons', render: rowData => rowData.ISHigh ? <label style={{ color: 'red' }}>{(posGroupId === 1) ? rowData.RiskName : rowData.ObservationReasons}</label> : (posGroupId === 1) ? rowData.RiskName : rowData.ObservationReasons },
            {
                field: 'EscalationList', title: 'Escalations', width: '5%', render: rowData => <Button variant="contained" color="secondary">{rowData.EscalationList.length}</Button>
            }
        ],
        data: []
    });
   
    const [otherData, setOtherData] = useState({
        columns: [
            { title: 'Attendant', field: 'Attendant' },
            { title: 'Time', field: 'DurationSeconds', defaultSort: 'desc', render: rowData => rowData.DurationTime },
            { title: 'Pre-determined Duty', field: 'PreDeterminedDuty'}
        ],
        data: []
    });

    const [tasksData, setTasksData] = useState({
        columns: [
            { title: 'Requested Start Date/Time', field: 'StartDate', defaultSort: 'desc'  },
            { title: 'Location', field: 'LocationPath', },
            { title: 'Patient', field: 'PatientName' },
            { title: (posGroupId === 1) ? 'Risk' : 'Observation Reasons', field: (posGroupId === 1) ? 'RiskName' : 'ObservationReasons', render: rowData => rowData.ISHigh ? <label style={{ color: 'red' }}>{(posGroupId === 1) ? rowData.RiskName : rowData.ObservationReasons}</label> : (posGroupId === 1) ? rowData.RiskName : rowData.ObservationReasons },
            { title: 'Requestor', field: 'Requestor' },
        ],
        data: []
    });

    const [escalationList, setEscalationList] = useState({
        columns: [
            { field: 'ObservationTimeString', title: 'Time' },
            { field: 'Observer', title: 'Observer' },
            { field: 'ObservationText', title: 'Observation', width: (posGroupId === 1) ? '25%' : '40%' },
            { field: 'InterventionText', title: 'Intervention', width: (posGroupId === 1) ? '25%' : '0%', hidden: (posGroupId === 2) },
            { field: 'LocationText', title: 'Location', width: (posGroupId === 1) ? '0%' : '20%', hidden: (posGroupId === 1) },
            { field: 'Comments', title: 'Comment', width: '10%', hidden: (posGroupId === 2) }
        ],
        data: []
    });

    const handleActiveRowClick = (event, rowData) => {
        if (rowData.EscalationList.length > 0) {
            setEscalationList({ ...escalationList, data: rowData.EscalationList });
            setIsShowEscalations(true);
        }
    }

    const getPatientSittingDashboard = () => {

        setBackDrop(true);
        HttpService.post('/PatientSitting/GetPatientSittingDashboard',
        {
            siteId: defaultSite.id
        })
        .then((result) => {
            setBackDrop(false);
            let items = result.data;
            setAvailableData({ ...availableData, data: items.Availables });
            setOnBreakData({ ...onBreakData, data: items.OnBreaks });
            setAssignData({ ...assignData, data: items.Assigns });
            setActiveData({ ...activeData, data: items.Actives });
            setOtherData({ ...otherData, data: items.Others });
            setTasksData({ ...tasksData, data: items.Tasks })
        },
        (error) => {
            console.log(error);
            setBackDrop(false);
        });
    };

    const moveItem = (posRoleAssignment, preDeterminedDuty, currentPreDeterminedDuty,beforePreDeterminedDuty) => {
        setBackDrop(true);
        HttpService.post('/PatientSitting/UpdatePatientSittingDashboardPreDeterminedDuty',
            {
                id: posRoleAssignment,
                siteId: defaultSite.id,
                preDeterminedDuty: preDeterminedDuty,
                currentPreDeterminedDuty: currentPreDeterminedDuty,
                beforePreDeterminedDuty: beforePreDeterminedDuty

            })
            .then((result) => {
                //Waiting Event Source Refresh
            },
            (error) => {
                console.log(error);
                setBackDrop(false);
            });    }

    useEffect(() => {
        getPatientSittingDashboard();

        if (window.PatientSittingDashboardEventSource) {
            window.PatientSittingDashboardEventSource.close();
        }

        window.PatientSittingDashboardEventSource = new EventSource(process.env.PUBLIC_URL+'/PatientSittingDashboardUpdate');
        window.PatientSittingDashboardEventSource.addEventListener('dashboard-update-notifications', message => {
            if (defaultSite.id === Number(message.data) || message.data === "SignInOut") {
                getPatientSittingDashboard();
            }
        });

        var interval = setInterval(function () { getPatientSittingDashboard() }, 60000);
        return () => {
            clearInterval(interval);
            window.PatientSittingDashboardEventSource.close();
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <React.Fragment>
            <ThemeProvider theme={currentTheme}>
                <CssBaseline />
                <Backdrop className={classes.backdrop} id="OBDashboardBackdrop" open={backDrop}>
                    <CircularProgress color="inherit" />
                </Backdrop>
                <ContentDialog open={isShowEscalations} title="Escalations" hideConfirm={true} maxWidth={"lg"} close={() => setIsShowEscalations(false)}>
                    <EscalationList escalationList={escalationList}></EscalationList>
                </ContentDialog>
                <AppBar style={{ background: currentTheme.primary, color: currentTheme.color}}>
                    <Toolbar>
                        <SmallMenu moduleName={"PatientSitting"} />
                        <Grid container direction="row" alignItems="center">
                            <Grid item>
                                <Typography variant="h6" >
                                    <Link to="/" className="nav-text">
                                        TeamLead
                                    </Link>
                                    / Patient Observation
                                </Typography>
                            </Grid>
                            <Grid item>
                                <StatusIcon />
                            </Grid>
                        </Grid>
                    </Toolbar>
                </AppBar>
               
                <Card color="primary" className="page-container" style={{ height: "100%" }}>
                    <CardBody className={'ml-auto'} style={{ marginTop: "50px" }}>
                        <Link to={{ pathname: "/PatientSittingList" }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                style={{background: currentTheme.secondary}}
                            >
                                Observations
                            </Button>
                        </Link>
                    </CardBody>
                    <CardBody style={{ color: currentTheme.text, background: currentTheme.background }}>
                        <div className={classes.root} autoComplete="off">
                            <Grid container direction="row" spacing={1}>
                                {/* Active*/}
                                <Grid item xs={12} sm={12} md={12}>
                                    <Card className={classes.card}>
                                        <CardContent>
                                            <MaterialTable
                                                style={tableStyleBase}
                                                title={<h3>Active ({activeData.data.length})</h3>}
                                                columns={activeData.columns}
                                                data={activeData.data}
                                                options={{
                                                    search: false,
                                                    paging: false,
                                                    minBodyHeight: "55vh", maxBodyHeight: "55vh"
                                                }}
                                                onRowClick={(event, rowData) => { handleActiveRowClick(event, rowData); }}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                {/* Assigned*/}
                                <Grid item xs={12} sm={12} md={12}>
                                    <Card className={classes.card}>
                                        <CardContent>
                                            <MaterialTable
                                                style={tableStyleBase}
                                                title={<h3>Assigned ({assignData.data.length})</h3>}
                                                columns={assignData.columns}
                                                data={assignData.data}
                                                options={{
                                                    search: false,
                                                    paging: false,
                                                    actionsColumnIndex: -1,
                                                    minBodyHeight: "55vh", maxBodyHeight: "55vh",
                                                    rowStyle: rowOrangeStyle
                                                }}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                {/* Available*/}
                                <Grid item xs={12} sm={5} md={5}>
                                    <Card className={classes.card}>
                                        <CardContent>
                                            <MaterialTable
                                                style={tableStyleBase}
                                                title={<h3>Available ({availableData.data.length})</h3>}
                                                columns={availableData.columns}
                                                data={availableData.data}
                                                options={{
                                                    search: false,
                                                    paging: false,
                                                    minBodyHeight: "55vh", maxBodyHeight: "55vh",
                                                    actionsColumnIndex: -1
                                                }}
                                                actions={[
                                                    {
                                                        icon: '',
                                                        tooltip: 'Change Status',
                                                        onClick: (event, rowData) => alert("You saved " + rowData.name)
                                                    }
                                                ]}
                                                components={{
                                                    Action: prop => (
                                                        <MenuPopupState {...prop} onMoveItem={moveItem} status={'Available'}></MenuPopupState>
                                                    ),
                                                }}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                {/* Other PObs Duties*/}
                                <Grid item xs={12} sm={7} md={7}>
                                    <Card className={classes.card}>
                                        <CardContent>
                                            <MaterialTable
                                                style={tableStyleBase}
                                                title={<h3>Others ({otherData.data.length})</h3>}
                                                columns={otherData.columns}
                                                data={otherData.data}
                                                options={{
                                                    search: false,
                                                    paging: false,
                                                    minBodyHeight: "55vh", maxBodyHeight: "55vh",
                                                    actionsColumnIndex: -1
                                                }}
                                                actions={[
                                                    {
                                                        icon: '',
                                                        tooltip: 'Change Status',
                                                        onClick: (event, rowData) => alert("You saved " + rowData.name)
                                                    }
                                                ]}
                                                components={{
                                                    Action: prop => (
                                                        <MenuPopupState {...prop} onMoveItem={moveItem} status={'Other'}></MenuPopupState>
                                                    ),
                                                }}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                                {/* On Break*/}
                                <Grid item xs={12} sm={12} md={12}>
                                    <Card className={classes.card}>
                                        <CardContent>
                                            <MaterialTable
                                                style={tableStyleBase}
                                                title={<h3>On Break ({onBreakData.data.length})</h3>}
                                                columns={onBreakData.columns}
                                                data={onBreakData.data}
                                                options={{
                                                    search: false,
                                                    paging: false,
                                                    minBodyHeight: "55vh", maxBodyHeight: "55vh"
                                                }}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                            <Grid container direction="row" spacing={1}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <Card className={classes.card}>
                                        <CardContent>
                                            <MaterialTable
                                                style={tableStyleBase}
                                                title={<h3>Pending Tasks ({tasksData.data.length})</h3>}
                                                columns={tasksData.columns}
                                                data={tasksData.data}
                                                options={{
                                                    paging: false,
                                                    minBodyHeight: "55vh", maxBodyHeight: "55vh"
                                                }}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </div>
                    </CardBody>
                </Card>
              
            </ThemeProvider>
        </React.Fragment>
    )
}